import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import StatusBadge from "@/components/StatusBadge";
import { StreakCard } from "@/components/StreakCard";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";
import { AnnouncementPinCard } from "@/components/AnnouncementPinCard";
import { UserCircle, FloppyDisk, EnvelopeSimple, Phone, Briefcase, CurrencyDollar, Cake } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Profile() {
    const { user, refreshUser } = useAuth();
    const [form, setForm] = useState({ full_name: "", phone: "", position: "", date_of_birth: "" });
    const [saving, setSaving] = useState(false);
    const dobLocked = Boolean((user?.date_of_birth || "").trim());

    useEffect(() => {
        if (!user) return;
        setForm({
            full_name: user.full_name || "",
            phone: user.phone || "",
            position: user.position || "",
            date_of_birth: user.date_of_birth || "",
        });
    }, [user]);

    const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { date_of_birth, ...rest } = form;
            const payload = dobLocked || !date_of_birth ? rest : { ...rest, date_of_birth };
            await api.patch("/users/me", payload);
            await refreshUser();
            toast.success("Profile updated successfully");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to update profile");
        } finally {
            setSaving(false);
        }
    };

    if (!user) return null;

    return (
        <div className="space-y-8 max-w-3xl">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">Profile</div>
                <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                    My Profile
                </h1>
                <p className="text-slate-600 mt-2 text-sm">Manage your personal information and view your account status.</p>
            </div>

            {/* Account summary */}
            <Card className="border-[#E2E8F0] bg-gradient-to-br from-[#003366] to-[#0066D9] text-white">
                <CardContent className="p-6 sm:p-8">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                        <div className="h-20 w-20 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center shrink-0">
                            <UserCircle size={48} weight="duotone" className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 mb-1">
                                {user.role}
                            </div>
                            <h2 className="text-2xl font-bold truncate" style={{ fontFamily: "Outfit" }}>
                                {user.full_name}
                            </h2>
                            <div className="text-sm text-white/80 mt-1">{user.email}</div>
                            <div className="flex flex-wrap gap-2 mt-3">
                                <StatusBadge status={user.approval_status} data-testid="profile-approval-status" />
                                <StatusBadge status={user.active ? "active" : "inactive"} data-testid="profile-active-status" />
                            </div>
                        </div>
                        {user.role === "technician" && (
                            <div className="sm:text-right">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">Pay rate</div>
                                <div className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>
                                    ${Number(user.pay_rate || 0).toFixed(2)}
                                    <span className="text-xs text-white/70 ml-1 font-normal">/ hour</span>
                                </div>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Work streak */}
            <StreakCard />

            {/* Editable info */}
            <Card className="border-[#E2E8F0]">
                <CardContent className="p-6 sm:p-8">
                    <h3 className="text-lg font-semibold text-[#1F2A44] mb-1" style={{ fontFamily: "Outfit" }}>
                        Personal information
                    </h3>
                    <p className="text-sm text-slate-500 mb-6">
                        Your pay rate and approval status can only be changed by an admin.
                    </p>

                    <form onSubmit={handleSave} className="space-y-5" data-testid="profile-form">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div className="space-y-2 sm:col-span-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Full name
                                </Label>
                                <Input
                                    value={form.full_name}
                                    onChange={handleChange("full_name")}
                                    className="h-11 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                    data-testid="profile-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Email (read-only)
                                </Label>
                                <div className="relative">
                                    <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <Input
                                        readOnly
                                        value={user.email}
                                        className="h-11 pl-9 border-[#E2E8F0] bg-slate-50 cursor-not-allowed"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Phone
                                </Label>
                                <div className="relative">
                                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <Input
                                        value={form.phone}
                                        onChange={handleChange("phone")}
                                        placeholder="(555) 123-4567"
                                        className="h-11 pl-9 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                        data-testid="profile-phone-input"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Position
                                </Label>
                                <div className="relative">
                                    <Briefcase size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <Input
                                        value={form.position}
                                        onChange={handleChange("position")}
                                        placeholder="e.g. Field Technician"
                                        className="h-11 pl-9 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                        data-testid="profile-position-input"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Date of birth{" "}
                                    {dobLocked && (
                                        <span className="normal-case tracking-normal font-normal text-slate-400">(read-only)</span>
                                    )}
                                </Label>
                                <div className="relative">
                                    <Cake size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <Input
                                        type="date"
                                        readOnly={dobLocked}
                                        max={new Date().toISOString().slice(0, 10)}
                                        value={form.date_of_birth}
                                        onChange={handleChange("date_of_birth")}
                                        className={`h-11 pl-9 border-[#E2E8F0] focus-visible:ring-[#0066D9] ${dobLocked ? "bg-slate-50 cursor-not-allowed" : ""}`}
                                        data-testid="profile-dob-input"
                                    />
                                </div>
                                <p className="text-xs text-slate-500" data-testid="profile-dob-helper">
                                    {dobLocked
                                        ? "Your date of birth is on file. Contact your admin if it needs to be corrected."
                                        : "Only the month and day are shared with your team — your birth year is never shown."}
                                </p>
                            </div>
                        </div>

                        {user.role === "technician" && (
                            <div className="bg-slate-50 border border-[#E2E8F0] rounded-md p-4 flex items-start gap-3">
                                <CurrencyDollar size={20} className="text-[#0066D9] shrink-0 mt-0.5" weight="duotone" />
                                <div className="text-sm">
                                    <div className="font-medium text-[#1F2A44]">
                                        Pay rate: ${Number(user.pay_rate || 0).toFixed(2)} / hour
                                    </div>
                                    <div className="text-slate-500 mt-0.5">
                                        Contact your admin to request a pay rate adjustment.
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="pt-2">
                            <Button
                                type="submit"
                                disabled={saving}
                                className="bg-[#0066D9] hover:bg-[#0052ad] text-white"
                                data-testid="profile-save-button"
                            >
                                <FloppyDisk size={16} className="mr-1.5" /> {saving ? "Saving..." : "Save changes"}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            <ChangePasswordCard />

            <AnnouncementPinCard />
        </div>
    );
}
